/**
 * メールアドレス生成サービス
 * Email Address Generator Service
 * 
 * 店舗別ユニークメールアドレスを生成・管理します
 */

import { ExternalPlatform, EmailAddressConfig } from '../types/external-sync';
import { logger } from '../utils/logger';

/**
 * デフォルト設定
 */
const DEFAULT_CONFIG: EmailAddressConfig = {
  domain: 'salonhub.co.jp',
  pattern: 'rsv-{storeId}-{platform}',
};

/**
 * プラットフォーム短縮形マッピング
 */
const PLATFORM_ABBREVIATIONS: Record<ExternalPlatform, string> = {
  hotpepper: 'hp',
  rakuten: 'rk',
  minimo: 'mn',
  epark: 'ep',
  kanzashi: 'kz',
};

/**
 * メールアドレス生成結果
 */
export interface GeneratedEmailAddress {
  fullAddress: string;
  localPart: string;
  domain: string;
  storeId: string;
  platform: ExternalPlatform;
  createdAt: Date;
}

/**
 * メールアドレス解析結果
 */
export interface ParsedEmailAddress {
  isValid: boolean;
  storeId?: string;
  platform?: ExternalPlatform;
  error?: string;
}

/**
 * メールアドレス生成サービスクラス
 */
export class EmailAddressGeneratorService {
  private config: EmailAddressConfig;
  private generatedAddresses: Map<string, GeneratedEmailAddress> = new Map();

  constructor(config?: Partial<EmailAddressConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.validateConfig();
  }

  /**
   * 設定を検証
   */
  private validateConfig(): void {
    if (!this.config.domain || this.config.domain.trim() === '') {
      throw new Error('Domain cannot be empty');
    }

    if (!this.config.pattern.includes('{storeId}') ||
        !this.config.pattern.includes('{platform}')) {
      throw new Error('Pattern must include {storeId} and {platform}');
    }
  }

  /**
   * 店舗・プラットフォーム用メールアドレスを生成
   * @param storeId 店舗ID
   * @param platform プラットフォーム
   * @returns 生成されたメールアドレス情報
   */
  public generateEmailAddress(storeId: string, platform: ExternalPlatform): GeneratedEmailAddress {
    try {
      // 入力値の検証
      this.validateInput(storeId, platform);

      // プラットフォーム短縮形を取得
      const platformAbbr = PLATFORM_ABBREVIATIONS[platform];

      // 固定アドレス生成（uniqueIdなし）
      const localPart = this.config.pattern
        .replace('{storeId}', storeId)
        .replace('{platform}', platformAbbr);

      // 完全なメールアドレスを構築
      const fullAddress = `${localPart}@${this.config.domain}`;

      // 生成されたアドレス情報
      const generatedAddress: GeneratedEmailAddress = {
        fullAddress,
        localPart,
        domain: this.config.domain,
        storeId,
        platform,
        createdAt: new Date(),
      };

      // キャッシュに保存
      const cacheKey = `${storeId}-${platform}`;
      this.generatedAddresses.set(cacheKey, generatedAddress);

      logger.info('Email address generated successfully', {
        storeId,
        platform,
        address: fullAddress,
      });

      return generatedAddress;

    } catch (error) {
      logger.error('Failed to generate email address', {
        storeId,
        platform,
        error,
      });
      throw error;
    }
  }

  /**
   * メールアドレスを解析して店舗・プラットフォーム情報を取得
   * @param emailAddress メールアドレス
   * @returns 解析結果
   */
  public parseEmailAddress(emailAddress: string): ParsedEmailAddress {
    try {
      // メールアドレス形式の基本検証
      if (!this.isValidEmailFormat(emailAddress)) {
        return {
          isValid: false,
          error: 'Invalid email format',
        };
      }

      const [localPart, domain] = emailAddress.split('@');

      // ドメインの確認
      if (domain !== this.config.domain) {
        return {
          isValid: false,
          error: 'Domain does not match',
        };
      }

      // パターンに基づいて解析
      const parseResult = this.parseLocalPart(localPart);

      if (!parseResult.isValid) {
        return parseResult;
      }

      logger.debug('Email address parsed successfully', {
        emailAddress,
        parseResult,
      });

      return parseResult;

    } catch (error) {
      logger.error('Failed to parse email address', {
        emailAddress,
        error,
      });
      return {
        isValid: false,
        error: 'Parsing error',
      };
    }
  }

  /**
   * 指定した店舗・プラットフォームの既存アドレスを取得
   * @param storeId 店舗ID
   * @param platform プラットフォーム
   * @returns 既存のメールアドレス情報（存在しない場合はnull）
   */
  public getExistingAddress(storeId: string, platform: ExternalPlatform): GeneratedEmailAddress | null {
    const cacheKey = `${storeId}-${platform}`;
    return this.generatedAddresses.get(cacheKey) || null;
  }

  /**
   * 複数のプラットフォーム用アドレスを一括生成
   * @param storeId 店舗ID
   * @param platforms プラットフォーム配列
   * @returns 生成されたアドレス情報の配列
   */
  public generateMultipleAddresses(
    storeId: string,
    platforms: ExternalPlatform[]
  ): GeneratedEmailAddress[] {
    const results: GeneratedEmailAddress[] = [];

    for (const platform of platforms) {
      try {
        const address = this.generateEmailAddress(storeId, platform);
        results.push(address);
      } catch (error) {
        logger.error(`Failed to generate address for ${platform}`, {
          storeId,
          platform,
          error,
        });
        // エラーがあっても他のプラットフォームの処理を続行
      }
    }

    return results;
  }


  /**
   * 入力値を検証
   */
  private validateInput(storeId: string, platform: ExternalPlatform): void {
    if (!storeId || storeId.trim() === '') {
      throw new Error('Store ID cannot be empty');
    }

    if (!/^[a-zA-Z0-9-_]+$/.test(storeId)) {
      throw new Error('Store ID can only contain alphanumeric characters, hyphens, and underscores');
    }

    if (!Object.values(PLATFORM_ABBREVIATIONS).includes(PLATFORM_ABBREVIATIONS[platform])) {
      throw new Error(`Unsupported platform: ${platform}`);
    }
  }


  /**
   * メールアドレス形式を検証
   */
  private isValidEmailFormat(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * ローカル部分を解析
   */
  private parseLocalPart(localPart: string): ParsedEmailAddress {
    // パターンを正規表現に変換（uniqueIdなし）
    const pattern = this.config.pattern
      .replace('{storeId}', '([a-zA-Z0-9-_]+)')
      .replace('{platform}', '([a-z]+)');

    const regex = new RegExp(`^${pattern}$`);
    const match = localPart.match(regex);

    if (!match) {
      return {
        isValid: false,
        error: 'Local part does not match expected pattern',
      };
    }

    const [, storeId, platformAbbr] = match;

    // プラットフォーム短縮形を逆引き
    const platform = this.getPlatformFromAbbreviation(platformAbbr);

    if (!platform) {
      return {
        isValid: false,
        error: `Unknown platform abbreviation: ${platformAbbr}`,
      };
    }

    return {
      isValid: true,
      storeId,
      platform,
    };
  }

  /**
   * プラットフォーム短縮形から正式名を取得
   */
  private getPlatformFromAbbreviation(abbreviation: string): ExternalPlatform | null {
    const entry = Object.entries(PLATFORM_ABBREVIATIONS)
      .find(([, abbr]) => abbr === abbreviation);
    
    return entry ? (entry[0] as ExternalPlatform) : null;
  }

  /**
   * 統計情報を取得
   */
  public getStatistics(): {
    totalGenerated: number;
    byPlatform: Record<ExternalPlatform, number>;
    byStore: Record<string, number>;
  } {
    const stats = {
      totalGenerated: this.generatedAddresses.size,
      byPlatform: {} as Record<ExternalPlatform, number>,
      byStore: {} as Record<string, number>,
    };

    // プラットフォーム別・店舗別の統計を計算
    Object.values(this.generatedAddresses).forEach((address: GeneratedEmailAddress) => {
      // プラットフォーム別
      stats.byPlatform[address.platform] = (stats.byPlatform[address.platform] || 0) + 1;

      // 店舗別
      stats.byStore[address.storeId] = (stats.byStore[address.storeId] || 0) + 1;
    });

    return stats;
  }

  /**
   * キャッシュをクリア
   */
  public clearCache(): void {
    this.generatedAddresses.clear();
    logger.info('Email address cache cleared');
  }
}

/**
 * シングルトンインスタンス
 */
export const emailAddressGenerator = new EmailAddressGeneratorService();

/**
 * ユーティリティ関数
 */
export const emailUtils = {
  /**
   * メールアドレスの有効性を検証
   */
  validateEmailAddress(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * メールアドレスを正規化
   */
  normalizeEmailAddress(email: string): string {
    return email.toLowerCase().trim();
  },

  /**
   * メールアドレスからドメインを抽出
   */
  extractDomain(email: string): string | null {
    const parts = email.split('@');
    return parts.length === 2 ? parts[1] : null;
  },

  /**
   * 安全なメールアドレス表示（プライバシー保護）
   */
  maskEmailAddress(email: string): string {
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;

    const maskedLocal = localPart.length > 3 
      ? `${localPart.substring(0, 2)}***${localPart.substring(localPart.length - 1)}`
      : '***';

    return `${maskedLocal}@${domain}`;
  },
};

export const emailAddressGeneratorService = EmailAddressGeneratorService;
export default EmailAddressGeneratorService;